import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  FormControl,
  Button,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  alpha
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Project import
import { gridSpacing } from 'config.js';

// Static data untuk Payroll
const payrollData = [
  {
    id: 1,
    employee: {
      name: 'Ahmad Rizki',
      photo: '',
      position: 'Software Developer',
      employeeId: 'EMP001'
    },
    period: 'Januari 2024',
    basicSalary: 8000000,
    allowances: 2000000,
    deductions: 500000,
    netSalary: 9500000,
    status: 'Paid',
    paymentDate: '2024-01-31',
    bankAccount: 'BCA - 1234567890'
  },
  {
    id: 2,
    employee: {
      name: 'Siti Nurhaliza',
      photo: '',
      position: 'HR Manager',
      employeeId: 'EMP002'
    },
    period: 'Januari 2024',
    basicSalary: 12000000,
    allowances: 3000000,
    deductions: 800000,
    netSalary: 14200000,
    status: 'Paid',
    paymentDate: '2024-01-31',
    bankAccount: 'Mandiri - 9876543210'
  },
  {
    id: 3,
    employee: {
      name: 'Budi Santoso',
      photo: '',
      position: 'Marketing Executive',
      employeeId: 'EMP003'
    },
    period: 'Januari 2024',
    basicSalary: 7000000,
    allowances: 1500000,
    deductions: 400000,
    netSalary: 8100000,
    status: 'Pending',
    paymentDate: '-',
    bankAccount: 'BNI - 1122334455'
  },
  {
    id: 4,
    employee: {
      name: 'Dewi Lestari',
      photo: '',
      position: 'Accountant',
      employeeId: 'EMP004'
    },
    period: 'Januari 2024',
    basicSalary: 9000000,
    allowances: 2000000,
    deductions: 600000,
    netSalary: 10400000,
    status: 'Paid',
    paymentDate: '2024-01-31',
    bankAccount: 'BCA - 5566778899'
  },
  {
    id: 5,
    employee: {
      name: 'Eko Prasetyo',
      photo: '',
      position: 'Sales Manager',
      employeeId: 'EMP005'
    },
    period: 'Januari 2024',
    basicSalary: 10000000,
    allowances: 2500000,
    deductions: 700000,
    netSalary: 11800000,
    status: 'Paid',
    paymentDate: '2024-01-31',
    bankAccount: 'Mandiri - 9988776655'
  },
  {
    id: 6,
    employee: {
      name: 'Fitri Handayani',
      photo: '',
      position: 'Designer',
      employeeId: 'EMP006'
    },
    period: 'Januari 2024',
    basicSalary: 7500000,
    allowances: 1800000,
    deductions: 450000,
    netSalary: 8850000,
    status: 'Pending',
    paymentDate: '-',
    bankAccount: 'BCA - 4433221100'
  },
  {
    id: 7,
    employee: {
      name: 'Gunawan Wijaya',
      photo: '',
      position: 'Operations Manager',
      employeeId: 'EMP007'
    },
    period: 'Januari 2024',
    basicSalary: 11000000,
    allowances: 2800000,
    deductions: 750000,
    netSalary: 13050000,
    status: 'Paid',
    paymentDate: '2024-01-31',
    bankAccount: 'BNI - 7788990011'
  },
  {
    id: 8,
    employee: {
      name: 'Hani Kartika',
      photo: '',
      position: 'Customer Service',
      employeeId: 'EMP008'
    },
    period: 'Januari 2024',
    basicSalary: 6000000,
    allowances: 1200000,
    deductions: 350000,
    netSalary: 6850000,
    status: 'Paid',
    paymentDate: '2024-01-31',
    bankAccount: 'BCA - 2233445566'
  }
];

const PayrollPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    // Simulate loading
    setLoading(true);
    setTimeout(() => {
      setData(payrollData);
      setLoading(false);
    }, 500);
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handlePeriodFilterChange = (event) => {
    setPeriodFilter(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredData = data
    .filter(record => {
      const matchesSearch = record.employee.name.toLowerCase().includes(searchText.toLowerCase()) ||
                           record.employee.position.toLowerCase().includes(searchText.toLowerCase()) ||
                           record.employee.employeeId.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus = statusFilter === '' || record.status === statusFilter;
      const matchesPeriod = periodFilter === '' || record.period === periodFilter;
      
      return matchesSearch && matchesStatus && matchesPeriod;
    })
    .sort((a, b) => new Date(b.paymentDate || '1900-01-01') - new Date(a.paymentDate || '1900-01-01'));

  const uniqueStatuses = [...new Set(data.map(record => record.status))];
  const uniquePeriods = [...new Set(data.map(record => record.period))];

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <PaymentIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Payroll
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola penggajian karyawan dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<RefreshIcon />} 
                onClick={() => window.location.reload()}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Refresh'}
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<AddIcon />} 
                onClick={() => navigate('/payroll/add')}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                ✨ Tambah Payroll
              </Button>
            </Box>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          {/* Filter Section */}
          <Box sx={{ 
            mb: 3, 
            p: 3, 
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Cari Karyawan"
                  variant="outlined"
                  value={searchText}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterIcon color="action" />
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Status</em>
                    </MenuItem>
                    {uniqueStatuses.map(status => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={periodFilter}
                    onChange={handlePeriodFilterChange}
                    displayEmpty
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Periode</em>
                    </MenuItem>
                    {uniquePeriods.map(period => (
                      <MenuItem key={period} value={period}>
                        {period}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {filteredData.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total Record
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table Section */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: 400,
                flexDirection: 'column',
                gap: 2
              }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  Memuat data payroll...
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          No
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          minWidth: 200
                        }}>
                          Karyawan
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Periode
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Gaji Pokok
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Tunjangan
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Potongan
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Gaji Bersih
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textAlign: 'center'
                        }}>
                          Aksi
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((record, index) => (
                        <TableRow 
                          key={record.id}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                              transform: 'scale(1.001)',
                              transition: 'all 0.2s ease'
                            },
                            '&:nth-of-type(even)': {
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                            }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            {index + 1 + page * rowsPerPage}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar 
                                sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                              >
                                {record.employee.name.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {record.employee.name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                  {record.employee.position}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                  {record.employee.employeeId}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {record.period}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              Rp {record.basicSalary.toLocaleString('id-ID')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                              + Rp {record.allowances.toLocaleString('id-ID')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                              - Rp {record.deductions.toLocaleString('id-ID')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AttachMoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', fontSize: '0.9rem' }}>
                                Rp {record.netSalary.toLocaleString('id-ID')}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={record.status}
                              color={getStatusColor(record.status)}
                              size="small"
                              icon={record.status === 'Paid' ? <CheckCircleIcon /> : <PendingIcon />}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/payroll/view/${record.id}`)}
                                sx={{ color: 'primary.main' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/payroll/edit/${record.id}`)}
                                sx={{ color: 'warning.main' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {}}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                  }}
                />
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayrollPage;
